// src/store/slices/orderSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as orderApi from '../api/orders';

// ─── Existing Thunks ────────────────────────────────────────────────────────

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await orderApi.getUserOrders(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch orders');
    }
  }
);

export const fetchOrderDetails = createAsyncThunk(
  'orders/fetchOrderDetails',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderApi.getOrderDetails(orderId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch order details');
    }
  }
);

export const cancelOrderAsync = createAsyncThunk(
  'orders/cancelOrder',
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const response = await orderApi.cancelOrder(orderId, { reason });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to cancel order');
    }
  }
);

// ─── New Thunks ──────────────────────────────────────────────────────────────

/**
 * Track order — fetches live Shiprocket tracking data + order status
 * Returns: { order, shipment, trackingHistory }
 */
export const trackOrderAsync = createAsyncThunk(
  'orders/trackOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderApi.trackOrder(orderId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to track order');
    }
  }
);

/**
 * Fetch shipment record for an order (AWB, courier name, tracking URL, status)
 * Separate from trackOrder — lighter call, used to show courier info in order detail
 */
export const fetchOrderShipments = createAsyncThunk(
  'orders/fetchOrderShipments',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderApi.getOrderShipments(orderId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch shipment details');
    }
  }
);

/**
 * Download invoice PDF — triggers browser file download
 * Only works for DELIVERED orders
 */
export const downloadInvoiceAsync = createAsyncThunk(
  'orders/downloadInvoice',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderApi.downloadInvoicePDF(orderId);

      // Extract filename from Content-Disposition header if present
      const disposition = response.headers['content-disposition'];
      let filename = `invoice-${orderId}.pdf`;
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '').trim();
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { orderId, filename };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to download invoice');
    }
  }
);

/**
 * Request return for a delivered order
 * @param {Object} { orderId, reason, items }
 */
export const requestReturnAsync = createAsyncThunk(
  'orders/requestReturn',
  async ({ orderId, reason, items = [] }, { rejectWithValue }) => {
    try {
      const response = await orderApi.requestReturn(orderId, { reason, items });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to submit return request');
    }
  }
);

/**
 * Check pincode serviceability before placing order
 * @param {String} pincode - 6-digit PIN code
 */
export const checkPincodeAsync = createAsyncThunk(
  'orders/checkPincode',
  async (pincode, { rejectWithValue }) => {
    try {
      const response = await orderApi.checkPincodeServiceability(pincode);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to check serviceability');
    }
  }
);

// ─── Initial State ───────────────────────────────────────────────────────────

const initialState = {
  orders: [],
  orderDetail: null,

  // Shipment / tracking data for the currently viewed order
  shipment: null,
  trackingData: null,

  // Pincode serviceability result
  pincodeServiceability: null,

  pagination: {
    total: 0,
    page: 1,
    pages: 1,
    limit: 10
  },

  loading: {
    orders: false,
    orderDetail: false,
    cancelOrder: false,
    tracking: false,
    shipment: false,
    invoice: false,
    return: false,
    pincode: false
  },

  error: {
    orders: null,
    orderDetail: null,
    cancelOrder: null,
    tracking: null,
    shipment: null,
    invoice: null,
    return: null,
    pincode: null
  },

  // Simple success flags — reset on next action
  success: {
    return: false,
    invoice: false
  }
};

// ─── Slice ───────────────────────────────────────────────────────────────────

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    clearOrderDetail: (state) => {
      state.orderDetail = null;
      state.shipment = null;
      state.trackingData = null;
    },
    clearTrackingData: (state) => {
      state.trackingData = null;
      state.shipment = null;
    },
    clearPincodeResult: (state) => {
      state.pincodeServiceability = null;
      state.error.pincode = null;
    },
    resetReturnSuccess: (state) => {
      state.success.return = false;
      state.error.return = null;
    },
    resetInvoiceSuccess: (state) => {
      state.success.invoice = false;
      state.error.invoice = null;
    }
  },

  extraReducers: (builder) => {

    // ── fetchOrders ──────────────────────────────────────────────────────────
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading.orders = true;
        state.error.orders = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.orders = action.payload.data.orders;
        state.pagination = action.payload.data.pagination;
        state.loading.orders = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading.orders = false;
        state.error.orders = action.payload?.message || 'Failed to fetch orders';
      });

    // ── fetchOrderDetails ────────────────────────────────────────────────────
    builder
      .addCase(fetchOrderDetails.pending, (state) => {
        state.loading.orderDetail = true;
        state.error.orderDetail = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.orderDetail = action.payload.data.order;
        state.loading.orderDetail = false;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.loading.orderDetail = false;
        state.error.orderDetail = action.payload?.message || 'Failed to fetch order details';
      });

    // ── cancelOrderAsync ─────────────────────────────────────────────────────
    builder
      .addCase(cancelOrderAsync.pending, (state) => {
        state.loading.cancelOrder = true;
        state.error.cancelOrder = null;
      })
      .addCase(cancelOrderAsync.fulfilled, (state, action) => {
        const orderId = action.meta.arg.orderId;

        // Update status in list
        const idx = state.orders.findIndex(o => o.orderId === orderId);
        if (idx !== -1) state.orders[idx].status = 'CANCELLED';

        // Update detail view if open
        if (state.orderDetail?.orderId === orderId) {
          state.orderDetail.status = 'CANCELLED';
          if (!state.orderDetail.timeline) state.orderDetail.timeline = [];
          state.orderDetail.timeline.push({
            status: 'CANCELLED',
            timestamp: new Date().toISOString(),
            note: action.meta.arg.reason || 'Cancelled by customer'
          });
        }

        state.loading.cancelOrder = false;
      })
      .addCase(cancelOrderAsync.rejected, (state, action) => {
        state.loading.cancelOrder = false;
        state.error.cancelOrder = action.payload?.message || 'Failed to cancel order';
      });

    // ── trackOrderAsync ──────────────────────────────────────────────────────
    builder
      .addCase(trackOrderAsync.pending, (state) => {
        state.loading.tracking = true;
        state.error.tracking = null;
      })
      .addCase(trackOrderAsync.fulfilled, (state, action) => {
        // Server returns: { data: { order, shipment, tracking } }
        state.trackingData = action.payload.data?.tracking || null;
        state.shipment = action.payload.data?.shipment || null;
        // Also update orderDetail if present
        if (action.payload.data?.order) {
          state.orderDetail = action.payload.data.order;
        }
        state.loading.tracking = false;
      })
      .addCase(trackOrderAsync.rejected, (state, action) => {
        state.loading.tracking = false;
        state.error.tracking = action.payload?.message || 'Failed to track order';
      });

    // ── fetchOrderShipments ──────────────────────────────────────────────────
    builder
      .addCase(fetchOrderShipments.pending, (state) => {
        state.loading.shipment = true;
        state.error.shipment = null;
      })
      .addCase(fetchOrderShipments.fulfilled, (state, action) => {
        // Server returns array; take the latest shipment
        const shipments = action.payload.data?.shipments || [];
        state.shipment = shipments.length > 0 ? shipments[0] : null;
        state.loading.shipment = false;
      })
      .addCase(fetchOrderShipments.rejected, (state, action) => {
        state.loading.shipment = false;
        state.error.shipment = action.payload?.message || 'Failed to fetch shipment';
      });

    // ── downloadInvoiceAsync ─────────────────────────────────────────────────
    builder
      .addCase(downloadInvoiceAsync.pending, (state) => {
        state.loading.invoice = true;
        state.error.invoice = null;
        state.success.invoice = false;
      })
      .addCase(downloadInvoiceAsync.fulfilled, (state) => {
        state.loading.invoice = false;
        state.success.invoice = true;
      })
      .addCase(downloadInvoiceAsync.rejected, (state, action) => {
        state.loading.invoice = false;
        state.error.invoice = action.payload?.message || 'Failed to download invoice';
      });

    // ── requestReturnAsync ───────────────────────────────────────────────────
    builder
      .addCase(requestReturnAsync.pending, (state) => {
        state.loading.return = true;
        state.error.return = null;
        state.success.return = false;
      })
      .addCase(requestReturnAsync.fulfilled, (state, action) => {
        state.loading.return = false;
        state.success.return = true;

        // Update orderDetail status to RETURNED if server confirms
        if (state.orderDetail) {
          state.orderDetail.status = 'RETURNED';
        }
      })
      .addCase(requestReturnAsync.rejected, (state, action) => {
        state.loading.return = false;
        state.error.return = action.payload?.message || 'Failed to submit return request';
      });

    // ── checkPincodeAsync ────────────────────────────────────────────────────
    builder
      .addCase(checkPincodeAsync.pending, (state) => {
        state.loading.pincode = true;
        state.error.pincode = null;
        state.pincodeServiceability = null;
      })
      .addCase(checkPincodeAsync.fulfilled, (state, action) => {
        state.pincodeServiceability = action.payload.data || null;
        state.loading.pincode = false;
      })
      .addCase(checkPincodeAsync.rejected, (state, action) => {
        state.loading.pincode = false;
        state.error.pincode = action.payload?.message || 'Pincode check failed';
      });
  }
});

export const {
  setPage,
  clearOrderDetail,
  clearTrackingData,
  clearPincodeResult,
  resetReturnSuccess,
  resetInvoiceSuccess
} = orderSlice.actions;

export default orderSlice.reducer;